import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  mapSystemEventDetailResponseToDto,
  mapSystemEventSummaryResponseToDto,
  mapSystemEventSupportBundleResponseToDto,
  mapSystemEventsListResponseToDto,
} from '../dto/system-events.dto.js';
import {
  buildEventDetailModel,
  buildEventTableRows,
  buildSystemEventQuery,
  buildSystemEventSummaryCards,
  getDefaultSystemEventFilters,
} from '../model/system-events.model.js';

const PAGE_LIMIT = 50;

export function useSystemEvents() {
  const { token } = useAuth();
  const initialFilters = getDefaultSystemEventFilters();
  const [summary, setSummary] = useState(() => mapSystemEventSummaryResponseToDto({}));
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const draftFiltersRef = useRef(initialFilters);
  const [nextCursor, setNextCursor] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detailEvent, setDetailEvent] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [supportBundle, setSupportBundle] = useState(null);
  const [isSupportLoading, setIsSupportLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    const response = await adminApi.getSystemEventSummary(token);
    setSummary(mapSystemEventSummaryResponseToDto(response));
  }, [token]);

  const loadEvents = useCallback(async ({ cursor = '', append = false } = {}) => {
    if (append) {
      setIsPageLoading(true);
    } else {
      setIsLoading(true);
    }
    setError('');

    try {
      const response = await adminApi.getSystemEvents(token, buildSystemEventQuery(filters, {
        limit: PAGE_LIMIT,
        cursor,
      }));
      const dto = mapSystemEventsListResponseToDto(response);
      setEvents((current) => (append ? [...current, ...dto.events] : dto.events));
      setNextCursor(dto.nextCursor);
    } catch (requestError) {
      if (!append) {
        setEvents([]);
        setNextCursor('');
      }
      setError(requestError?.message || 'Failed to load system events.');
    } finally {
      setIsLoading(false);
      setIsPageLoading(false);
    }
  }, [token, filters]);

  const refresh = useCallback(async () => {
    setSuccess('');
    try {
      await Promise.all([
        loadSummary(),
        loadEvents(),
      ]);
    } catch (requestError) {
      setSummary(mapSystemEventSummaryResponseToDto({}));
      setError(requestError?.message || 'Failed to load system events.');
      setIsLoading(false);
    }
  }, [loadSummary, loadEvents]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateFilter = useCallback((name, value) => {
    draftFiltersRef.current = {
      ...draftFiltersRef.current,
      [name]: value,
    };
    setDraftFilters((current) => ({
      ...current,
      [name]: value,
    }));
  }, []);

  const applyFilters = useCallback(() => {
    setFilters(draftFiltersRef.current);
  }, []);

  const resetFilters = useCallback(() => {
    const nextFilters = getDefaultSystemEventFilters();
    draftFiltersRef.current = nextFilters;
    setDraftFilters(nextFilters);
    setFilters(nextFilters);
  }, []);

  const loadNextPage = useCallback(() => {
    if (!nextCursor || isPageLoading) {
      return Promise.resolve();
    }

    return loadEvents({ cursor: nextCursor, append: true });
  }, [nextCursor, isPageLoading, loadEvents]);

  const openDetail = useCallback(async (event) => {
    const eventId = event?.id;
    if (!eventId) {
      return;
    }

    setIsDetailOpen(true);
    setDetailEvent(buildEventDetailModel(event));
    setDetailError('');
    setSupportBundle(null);
    setIsDetailLoading(true);

    try {
      const response = await adminApi.getSystemEvent(token, eventId);
      setDetailEvent(buildEventDetailModel(mapSystemEventDetailResponseToDto(response)));
    } catch (requestError) {
      setDetailError(requestError?.message || 'Failed to load event detail.');
    } finally {
      setIsDetailLoading(false);
    }
  }, [token]);

  const closeDetail = useCallback(() => {
    setIsDetailOpen(false);
    setDetailEvent(null);
    setDetailError('');
    setSupportBundle(null);
  }, []);

  const createSupportReference = useCallback(async () => {
    if (!detailEvent?.id) {
      return;
    }

    setIsSupportLoading(true);
    setDetailError('');
    setSuccess('');

    try {
      const response = await adminApi.createSystemEventSupportBundle(token, detailEvent.id);
      const bundle = mapSystemEventSupportBundleResponseToDto(response);
      setSupportBundle(bundle);
      if (navigator?.clipboard?.writeText && bundle.supportReference) {
        await navigator.clipboard.writeText(bundle.supportReference);
        setSuccess('Support reference copied.');
      } else if (bundle.supportReference) {
        setSuccess('Support reference created.');
      }
    } catch (requestError) {
      setDetailError(requestError?.message || 'Failed to create support reference.');
    } finally {
      setIsSupportLoading(false);
    }
  }, [token, detailEvent]);

  return {
    summary,
    summaryCards: buildSystemEventSummaryCards(summary),
    events,
    rows: buildEventTableRows(events),
    filters: draftFilters,
    appliedFilters: filters,
    nextCursor,
    isLoading,
    isPageLoading,
    error,
    success,
    detailEvent,
    isDetailOpen,
    isDetailLoading,
    detailError,
    supportBundle,
    isSupportLoading,
    updateFilter,
    applyFilters,
    resetFilters,
    refresh,
    loadNextPage,
    openDetail,
    closeDetail,
    createSupportReference,
  };
}
